import { useQuery } from '@tanstack/react-query';
import { profileLinksApi, type ProfileLinksApi } from './profile-links-api';
import { isNotImplemented } from './profile-links-error';
import { queryClient, queryKeys } from './query-client';
import type { ProfileLink, ProfileLinksList } from './profile-links-types';

/**
 * React Query bindings for the owner screens. Reads are queries; writes follow the house style — call the
 * API, then write the server's echo into the cache (no optimistic copy: a link's public state must never
 * read as published before the service said so).
 *
 * A `501 not_implemented` (Phase 3 not deployed) is not retried: it is an answer, not a blip.
 */
const retry = (count: number, err: unknown) => !isNotImplemented(err) && count < 2;

export function useProfileLinksList(enabled = true, api: ProfileLinksApi = profileLinksApi) {
  return useQuery({ queryKey: queryKeys.profileLinks, queryFn: () => api.list(), enabled, retry });
}

export function useProfileLinkLeads(linkId: string | null, enabled = true, api: ProfileLinksApi = profileLinksApi) {
  return useQuery({
    queryKey: queryKeys.profileLinkLeads(linkId ?? ''),
    queryFn: () => api.leads(linkId as string),
    enabled: enabled && !!linkId,
    retry,
  });
}

/** Put the server's copy of one link into the list cache (insert when new). */
export function cacheProfileLink(link: ProfileLink): void {
  queryClient.setQueryData<ProfileLinksList>(queryKeys.profileLinks, (current) => {
    if (!current) return { handle: link.handle, links: [link] };
    const exists = current.links.some((l) => l.id === link.id);
    return { ...current, links: exists ? current.links.map((l) => (l.id === link.id ? link : l)) : [...current.links, link] };
  });
}

export function cacheProfileHandle(handle: string): void {
  queryClient.setQueryData<ProfileLinksList>(queryKeys.profileLinks, (current) => ({ links: current?.links ?? [], handle }));
}
